/**
 * Токены клавиш, у которых подпись зависит от платформы либо рисуется глифом.
 * Всё остальное («K», «F5», «Backspace», «PageDown») передаётся строкой и рисуется как есть —
 * таблицу на всю раскладку тут никто не держит.
 */
export type KbdToken =
  'mod' | 'ctrl' | 'alt' | 'shift' | 'enter' | 'esc' | 'tab' | 'up' | 'down' | 'left' | 'right'

/**
 * Клавиша сочетания: токен из таблицы либо произвольная подпись.
 * `string & {}` — идиома «union с подсказками»: без неё TypeScript схлопывает объединение
 * в string и автодополнение по токенам пропадает.
 */
export type KbdKey = KbdToken | (string & {})

/**
 * Подписи колпачков. Слева — то, что напечатано на клавиатуре Apple, справа — на PC.
 * Совпадение неслучайное: глиф остаётся ровно там, где его знает системный скринридер
 * (VoiceOver читает ⌘ как «command»), а NVDA получает слово.
 */
const KEY_CAPS: Record<KbdToken, { apple: string; other: string }> = {
  mod: { apple: '⌘', other: 'Ctrl' },
  // на PC отдельного Control нет: mod и ctrl там одна и та же физическая клавиша
  ctrl: { apple: '⌃', other: 'Ctrl' },
  alt: { apple: '⌥', other: 'Alt' },
  shift: { apple: '⇧', other: 'Shift' },
  enter: { apple: '↵', other: 'Enter' },
  esc: { apple: 'esc', other: 'Esc' },
  tab: { apple: '⇥', other: 'Tab' },
  up: { apple: '↑', other: '↑' },
  down: { apple: '↓', other: '↓' },
  left: { apple: '←', other: '←' },
  right: { apple: '→', other: '→' },
}

function isToken(key: string): key is KbdToken {
  return Object.prototype.hasOwnProperty.call(KEY_CAPS, key)
}

/** Токен → подпись клавиши текущей платформы. Не токен → строка как есть. */
export function resolveKey(key: KbdKey, apple: boolean): string {
  if (isToken(key)) {
    const cap = KEY_CAPS[key]
    return apple ? cap.apple : cap.other
  }

  // Колпачок подписан заглавной: без нормализации в одном месте системы окажется ⌘K, в соседнем ⌘k
  return key.length === 1 ? key.toUpperCase() : key
}

/**
 * Разделитель клавиш в сочетании. На Apple его нет («⌘K»), на PC это «+» («Ctrl+K») —
 * обе записи родные для своей платформы, поэтому пропом не настраивается.
 */
export function keySeparator(apple: boolean): string {
  return apple ? '' : '+'
}
